import {groupOffersMedical, type FaqItem, type FaqSection, type GroupConfig} from '../types';

export const MEDICAL_REQUIRED_ITEM: FaqItem = {
  question: 'Is medical coverage required?',
  answer:
    'No, but the Affordable Care Act (ACA) requires us to document your declination if you choose not to enroll. Declining means you cannot enroll again until the next Open Enrollment, unless you have a qualifying life event.',
};

// Limited-medical-only sites have no ACA declination to explain.
export const LIMITED_MEDICAL_REQUIRED_ITEM: FaqItem = {
  question: 'Is limited medical coverage required?',
  answer: 'No. Limited medical is optional, and you can still enroll in any other benefits offered.',
};

/** Shared FAQ copy for Concierge-administered groups. A site renders these until it sets its own `faq`. */
export const DEFAULT_FAQ_SECTIONS: FaqSection[] = [
  {
    label: 'Open Enrollment',
    items: [
      {
        question: 'When does Open Enrollment end?',
        answer:
          "Open enrollment dates vary by location. Any changes you make during your group's Open Enrollment take effect at the start of your plan year. Please communicate with your Human Resources office to confirm the dates for your location.",
      },
      {
        question: "What if I'm already enrolled?",
        answer:
          "If you're currently enrolled and don't need to make any changes, your plan benefits will automatically roll over. No action is required during Open Enrollment unless you want to change plans, add or remove dependents, or decline coverage.",
      },
      {
        question: 'How do I make changes during Open Enrollment?',
        answer: 'Open Enrollment is the time to update your coverage. You can make changes:',
        bullets: [
          'Directly through the enrollment website (click \u201CEnroll Now\u201D)',
          'By emailing <email>cs@ctpa.com</email> or <email>eligibility@ctpa.com</email>',
        ],
        closing:
          'Change request emails must include your full name, date of birth, and a summary of the requested change.',
      },
    ],
  },
  {
    label: 'Plans & Coverage',
    items: [
      {
        question: 'What happens if I want to change my benefit plan after Open Enrollment closes?',
        answer:
          'Once enrolled, you cannot make plan changes except in the event of a qualifying life event (marriage, birth of a child, divorce, loss of other coverage, etc.). Please see your Human Resources Representative for information on your HIPAA Rights Notice and further coverage enrollment and termination options available to you.',
      },
      {
        question: 'How do I find a provider?',
        answer:
          'Finding a qualified provider is simple — you can make an appointment using the <app>Clever Health app</app>. You can also visit <url>firsthealthlbp.com</url> to search FirstHealth PPO network providers in your area.',
      },
      MEDICAL_REQUIRED_ITEM,
    ],
  },
  {
    label: 'After You Enroll',
    items: [
      {
        question: 'What do I need to know about my ID card?',
        answer:
          "You'll receive an electronic ID card from us via email or text once your coverage starts. You can print copies of your ID card or access them on your phone via the <app>Clever Health app</app>.",
      },
      {
        question: 'Why did I receive an Explanation of Benefits (EOB) in the mail?',
        answer:
          'An EOB is not a bill. It simply outlines the total charges for your visit and what your health plan covers. EOBs can also be viewed in the <app>Clever Health app</app> or via the web portal.',
      },
    ],
  },
  {
    label: 'Getting Help',
    items: [
      {
        question: 'Who do I call with questions about my benefits or ID card?',
        answer: 'You have a few options:',
        bullets: [
          'Text us at <tel>918.876.5015</tel> for fast answers',
          'Call the Concierge Team at <tel>888.820.5687</tel>',
          'Email <email>cs@ctpa.com</email>',
        ],
        closing: "If you're requesting information, we'll email or text you directly.",
      },
      {
        question: 'Who do I contact about prescriptions?',
        answer:
          "For prescription questions, call the Concierge Team at <tel>888.820.5687</tel> or email <email>cs@ctpa.com</email>. They'll connect you with the right person and can help you find a participating pharmacy, check the formulary, or answer questions about your medications.",
      },
    ],
  },
];

export function siteFaq(group: GroupConfig): FaqSection[] {
  if (group.faq) return group.faq;
  if (groupOffersMedical(group)) return DEFAULT_FAQ_SECTIONS;
  return DEFAULT_FAQ_SECTIONS.map((section) => ({
    ...section,
    items: section.items.map((item) => (item === MEDICAL_REQUIRED_ITEM ? LIMITED_MEDICAL_REQUIRED_ITEM : item)),
  }));
}
